import { useEffect, useRef } from 'react';
import {
  Modal,
  ChatPanel,
  MessageBubble,
  Composer,
  IconButton,
  Icon,
  icons,
  Button,
  Callout,
  theme,
  type Message,
} from '@agentisai/design-system';
import { buttonRow, dialog as dialogCls, dialogChatPanel } from './CreateWorkstreamPanel.css';

const CW_AGENT_INTRO: Message = {
  id: 'cw-agent-1',
  memberId: 'workstream-agent',
  memberName: 'Workstream Agent',
  memberRole: '',
  memberType: 'agent',
  variant: 'mira',
  text: "Let's set up your workstream — in a sentence, what outcome are you driving?",
  time: 'Just now',
};

export interface CreateWorkstreamPanelProps {
  open: boolean;
  onClose: () => void;
  /** Conversation transcript (DS Message objects). Rendered after the seeded intro. */
  messages?: Message[];
  /**
   * Preformatted display time for the seeded agent intro bubble (the parent
   * stamps it at panel-open and formats it through the same formatter as the
   * transcript, so it ages consistently). Defaults to `'Just now'`.
   */
  introTime?: string;
  value?: string;
  onChange?: (value: string) => void;
  onSend?: (value: string) => void;
  sendDisabled?: boolean;
  /**
   * Max characters the composer value may have (the shared `MAX_TEXT_LEN`
   * contract limit). When `value` exceeds it, an inline over-limit message shows
   * — so the user gets feedback BEFORE sending, not a post-send server rejection.
   * The parent drives `sendDisabled` from the same check. Omit to skip it.
   */
  maxLength?: number;
  /** In-flight chat-turn indicator — disables send while true. */
  pending?: boolean;
  onCreate?: () => void;
  createDisabled?: boolean;
  creating?: boolean;
  /** Error message from the send-chat-turn mutation (rendered as a live region). */
  sendError?: string | null;
  /** Error message from the create-workstream mutation (rendered as a live region). */
  createError?: string | null;
}

/**
 * CreateWorkstreamPanel — one-off page composition for the "Create new
 * workstream" surface. Composes DS Modal (focus trap, Escape, backdrop
 * dismiss) + ChatPanel fluid + MessageBubble (seeded agent intro + transcript)
 * + Composer (empty-tenant placeholder) + Create affordance.
 *
 * State is fully owned by the parent (HomePage). This component renders only.
 * S16 drives the conversation props via two useMutations in HomePage.
 */
export function CreateWorkstreamPanel({
  open,
  onClose,
  messages = [],
  introTime = 'Just now',
  value,
  onChange,
  onSend,
  sendDisabled,
  maxLength,
  pending = false,
  onCreate,
  createDisabled = true,
  creating = false,
  sendError,
  createError,
}: CreateWorkstreamPanelProps) {
  // Auto-scroll the message list to the bottom whenever a new chat turn lands
  // (the server reply, the user echo, or the create confirmation) — or when
  // the awaiting-reply typing indicator appears — so the latest content is
  // visible. A sentinel at the end of the messages slot locates the
  // ChatPanel's `overflow-y:auto` message list, which is then pinned to its
  // full scroll height (past the list's bottom padding + the typing-indicator
  // lozenge, so the newest item sits flush at the bottom). Imperative DOM
  // sync — the sanctioned use of an effect + ref (frontend-architecture
  // R24/R25), not data-in-effect.
  const bottomRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    let el = bottomRef.current?.parentElement ?? null;
    while (el && el.scrollHeight <= el.clientHeight) el = el.parentElement;
    if (el) el.scrollTop = el.scrollHeight;
  }, [open, messages.length, pending]);

  const composerLeadingActions = (
    <>
      <IconButton
        aria-label="Attach file"
        size="sm"
        variant="ghost"
        icon={<Icon as={icons.Paperclip} size={14} />}
      />
      <IconButton
        aria-label="Quick actions"
        size="sm"
        variant="ghost"
        icon={<Icon as={icons.Zap} size={14} />}
      />
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create New Workstream"
      size="lg"
      flushBody
      className={dialogCls}
    >
      <ChatPanel
        fluid
        className={dialogChatPanel}
        caption="Guided by the Workstream Agent"
        awaitingReply={pending}
        messages={
          <>
            <MessageBubble
              key={CW_AGENT_INTRO.id}
              message={{ ...CW_AGENT_INTRO, time: introTime }}
            />
            {messages.map((m) => (
              <MessageBubble key={m.id} message={m} />
            ))}
            {/* Scroll anchor — kept in view as new turns arrive. */}
            <div ref={bottomRef} aria-hidden="true" />
          </>
        }
        composer={
          <>
            {sendError && (
              <div
                role="alert"
                aria-live="assertive"
                style={{ padding: `0 ${theme.space[4]} ${theme.space[2]}` }}
              >
                <Callout tone="danger">{sendError}</Callout>
              </div>
            )}
            <Composer
              value={value}
              onChange={onChange}
              onSend={onSend}
              sendDisabled={sendDisabled}
              placeholder="Click here to begin…"
              leadingActions={composerLeadingActions}
            />
            {maxLength != null && (value?.length ?? 0) > maxLength && (
              <div
                role="alert"
                aria-live="assertive"
                style={{ padding: `${theme.space[2]} ${theme.space[4]} 0` }}
              >
                <Callout tone="danger">
                  Message is too long — {(value?.length ?? 0).toLocaleString()} of{' '}
                  {maxLength.toLocaleString()} characters. Shorten it to send.
                </Callout>
              </div>
            )}
            {createError && (
              <div
                role="alert"
                aria-live="assertive"
                style={{ padding: `${theme.space[2]} ${theme.space[4]} 0` }}
              >
                <Callout tone="danger">{createError}</Callout>
              </div>
            )}
            <div className={buttonRow}>
              <Button variant="primary" disabled={createDisabled || creating} onClick={onCreate}>
                {creating ? 'Creating…' : 'Create workstream'}
              </Button>
            </div>
          </>
        }
      />
    </Modal>
  );
}
CreateWorkstreamPanel.displayName = 'CreateWorkstreamPanel';
